import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { CloudErrors, FlowjoCommons } from '@portal/portal-core';

import { FjAvatar, FjInput } from 'components/primitives';
import { selectCurrentUser, selectUserId, alertsSelector } from 'selectors';
import { getUserInitials, composeValidators, getCountryOptions } from 'utils';
import { validateMinLength, validateMaxLength } from 'utils/validators';
import { updateUserProfile, changeUserPassword } from 'ducks/user-client/actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const { Regional } = FlowjoCommons;

// Minimal shape of the fields this component reads off the current user.
interface CurrentUser {
  firstname?: string;
  lastname?: string;
  countryCode?: string;
  primaryEmail?: string;
}

// Alerts map keyed by cloud error type; presence of a key signals that alert.
type Alerts = Record<string, unknown> | null | undefined;

interface CountryOption {
  value: string;
  label: string;
}

interface ProfileUpdates {
  firstname?: string;
  lastname?: string;
  countryCode?: string;
}

// Shape returned by the untyped `composeValidators` util (JSDoc types it as `object`).
interface ComposedValidators {
  validator: (val: string) => boolean;
  getValidationMessage: (val: string) => string;
}

// The element type `composeValidators` accepts (its `Validator` interface is not exported).
// The built-in validators declare `(val: string)` params, which is contravariantly
// incompatible with the util's `(val: unknown)` signature; the cast bridges that gap.
type ComposeArg = Parameters<typeof composeValidators>[number];

const UserDrawer = (): React.JSX.Element => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser) as CurrentUser;
  const userId = useAppSelector(selectUserId) as string;
  const alerts = useAppSelector(alertsSelector) as Alerts;

  const dispatch = useAppDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [firstname, setFirstname] = useState(currentUser.firstname || '');
  const [lastname, setLastname] = useState(currentUser.lastname || '');
  const [countryCode, setCountryCode] = useState(currentUser.countryCode || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset password fields on successful password change (was componentDidUpdate).
  // prevAlertsRef makes the first run a no-op, matching componentDidUpdate.
  const prevAlertsRef = useRef<Alerts>(alerts);
  useEffect(() => {
    const prevAlerts = prevAlertsRef.current;
    if (
      prevAlerts &&
      alerts &&
      !prevAlerts[CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD] &&
      alerts[CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD]
    ) {
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
    prevAlertsRef.current = alerts;
  }, [alerts]);

  // Reset isSaving flag when currentUser is updated (profile save completed).
  const prevCurrentUserRef = useRef<CurrentUser>(currentUser);
  useEffect(() => {
    const prevCurrentUser = prevCurrentUserRef.current;
    if (prevCurrentUser !== currentUser && isSaving) {
      setIsSaving(false);
      // Clear the safety timeout if it exists
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
        saveTimeout.current = null;
      }
    }
    prevCurrentUserRef.current = currentUser;
  }, [currentUser, isSaving]);

  // Clean up timeout when component unmounts
  useEffect(
    () => () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    },
    [],
  );

  const handleEditToggle = (): void => {
    setIsEditing((prev) => !prev);
    setFirstname(currentUser.firstname || '');
    setLastname(currentUser.lastname || '');
    setCountryCode(currentUser.countryCode || '');
  };

  const handlePasswordToggle = (): void => {
    setIsChangingPassword((prev) => !prev);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSaveProfile = (): void => {
    // Prevent multiple simultaneous saves
    if (isSaving) {
      return;
    }

    // Only include fields that have actually changed
    const updates: ProfileUpdates = {};
    if (firstname !== currentUser.firstname) {
      updates.firstname = firstname;
    }
    if (lastname !== currentUser.lastname) {
      updates.lastname = lastname;
    }
    if (countryCode !== currentUser.countryCode) {
      updates.countryCode = countryCode;
    }

    // Only dispatch action if there are actual changes
    if (Object.keys(updates).length > 0) {
      setIsSaving(true);
      setIsEditing(false);
      dispatch(updateUserProfile(userId, updates));

      // Safety timeout to reset isSaving flag in case the update fails silently
      saveTimeout.current = setTimeout(() => {
        setIsSaving(false);
      }, 10000); // 10 second timeout
    } else {
      setIsEditing(false);
    }
  };

  const isPasswordValid = (): boolean =>
    currentPassword.length >= 8 &&
    currentPassword.length <= 256 &&
    newPassword.length >= 8 &&
    newPassword.length <= 256 &&
    newPassword === confirmPassword;

  const handleChangePassword = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (isPasswordValid()) {
      dispatch(changeUserPassword(userId, currentPassword, newPassword));
    }
  };

  const renderViewMode = (): React.JSX.Element => {
    const { primaryEmail } = currentUser;
    const countryDisplay = countryCode
      ? `${Regional.getCountryName(countryCode)} (${countryCode})`
      : 'Not set';

    return (
      <InfoContainer>
        <FjAvatar
          boxSize={60}
          fjAvatarStyle={{
            backgroundColor: theme.colors.avatarBgDark,
            color: theme.colors.white,
            fontSize: theme.fontSizes.h1,
            fontWeight: 600,
          }}
          initials={getUserInitials({ firstname, lastname })}
        />
        <Username>
          {firstname} {lastname}
        </Username>
        <Email>{primaryEmail}</Email>
        <CountryCode>{countryDisplay}</CountryCode>

        <ButtonGroup>
          <ActionButton onClick={handleEditToggle}>Edit Profile</ActionButton>
          <ActionButton onClick={handlePasswordToggle}>Change Password</ActionButton>
        </ButtonGroup>
      </InfoContainer>
    );
  };

  const renderEditMode = (): React.JSX.Element => (
    <InfoContainer>
      <FjAvatar
        boxSize={60}
        fjAvatarStyle={{
          backgroundColor: theme.colors.avatarBgDark,
          color: theme.colors.white,
          fontSize: theme.fontSizes.h1,
          fontWeight: 600,
        }}
        initials={getUserInitials({ firstname, lastname })}
      />

      <FormSection>
        <Label>First Name</Label>
        <FjInput
          value={firstname}
          onChange={(value) => setFirstname(value)}
          placeholder="First Name"
          height={35}
        />
      </FormSection>

      <FormSection>
        <Label>Last Name</Label>
        <FjInput
          value={lastname}
          onChange={(value) => setLastname(value)}
          placeholder="Last Name"
          height={35}
        />
      </FormSection>

      <FormSection>
        <Label>Country</Label>
        <CountrySelect value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
          <option value="">Select a country</option>
          {getCountryOptions().map((option: CountryOption) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </CountrySelect>
      </FormSection>

      <Email>{currentUser.primaryEmail}</Email>

      <ButtonGroup>
        <ActionButton onClick={handleSaveProfile} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </ActionButton>
        <SecondaryButton onClick={handleEditToggle} disabled={isSaving}>
          Cancel
        </SecondaryButton>
      </ButtonGroup>
    </InfoContainer>
  );

  const renderPasswordChange = (): React.JSX.Element => {
    const passwordValidators = composeValidators(
      validateMinLength(8) as ComposeArg,
      validateMaxLength(256) as ComposeArg,
    ) as ComposedValidators;

    const confirmValidators = composeValidators(
      validateMinLength(8) as ComposeArg,
      validateMaxLength(256) as ComposeArg,
      {
        validator: (val: string) => val === newPassword,
        getValidationMessage: () => 'Passwords do not match.',
      } as ComposeArg,
    ) as ComposedValidators;

    const isValid = isPasswordValid();
    const passwordChanged = !!(
      alerts && alerts[CloudErrors.cloudErrorTypes.SUCCESS_CHANGE_PASSWORD]
    );
    const passwordError = !!(alerts && alerts[CloudErrors.cloudErrorTypes.CANNOT_CHANGE_PASSWORD]);

    return (
      <InfoContainer>
        <SectionTitle>Change Password</SectionTitle>

        {passwordChanged && (
          <SuccessMessage>Your password was successfully changed.</SuccessMessage>
        )}

        {passwordError && (
          <ErrorMessage>
            Unable to change password. Please check your current password.
          </ErrorMessage>
        )}

        <form onSubmit={handleChangePassword}>
          <FormSection>
            <Label>Current Password</Label>
            <FjInput
              type="password"
              value={currentPassword}
              onChange={(value) => setCurrentPassword(value)}
              placeholder="Current Password"
              autoComplete="current-password"
              height={35}
              validator={passwordValidators.validator}
              getValidationMessage={passwordValidators.getValidationMessage}
            />
          </FormSection>

          <FormSection>
            <Label>New Password</Label>
            <FjInput
              type="password"
              value={newPassword}
              onChange={(value) => setNewPassword(value)}
              placeholder="New Password"
              autoComplete="new-password"
              height={35}
              validator={passwordValidators.validator}
              getValidationMessage={passwordValidators.getValidationMessage}
            />
          </FormSection>

          <FormSection>
            <Label>Confirm New Password</Label>
            <FjInput
              type="password"
              value={confirmPassword}
              onChange={(value) => setConfirmPassword(value)}
              placeholder="Confirm New Password"
              autoComplete="new-password"
              height={35}
              validator={confirmValidators.validator}
              getValidationMessage={confirmValidators.getValidationMessage}
            />
          </FormSection>

          <ButtonGroup>
            <ActionButton type="submit" disabled={!isValid}>
              Update Password
            </ActionButton>
            <SecondaryButton type="button" onClick={handlePasswordToggle}>
              Cancel
            </SecondaryButton>
          </ButtonGroup>
        </form>
      </InfoContainer>
    );
  };

  if (isChangingPassword) {
    return renderPasswordChange();
  }
  if (isEditing) {
    return renderEditMode();
  }
  return renderViewMode();
};

export default UserDrawer;

const InfoContainer = styled.div`
  color: black;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Username = styled.p`
  font-size: 20px;
  font-weight: bold;
  margin: 15px 0 7px 0;
`;

const Email = styled.p`
  font-size: 16px;
  margin: 7px 0;
  color: #666;
`;

const CountryCode = styled.p`
  font-size: 16px;
  margin: 7px 0;
  color: #666;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 300px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  background-color: #156777;
  color: white;
  border: 1px solid #156777;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #124f5d;
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  background-color: white;
  color: #156777;
  border: 1px solid #156777;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const FormSection = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 10px 0;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
`;

const CountrySelect = styled.select`
  width: 100%;
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  height: 35px;

  &:focus {
    outline: none;
    border-color: #156777;
    box-shadow: 0 0 0 2px rgba(21, 103, 119, 0.1);
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 10px 0 20px 0;
  color: #333;
`;

const SuccessMessage = styled.div`
  padding: 10px;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;
